'use client'

import { Play } from 'lucide-react'
import React, { useEffect, useRef, useState } from 'react'

import { getOptimizedImageUrl } from '@/utilities/getOptimizedImageUrl'

type Props = {
  /** Trusted CMS iframe HTML (e.g. `<iframe src="https://my.matterport.com/show/?m=…">`). */
  embedHtml: string
  /** Poster: a representative project photo of the same space (served via the image optimizer). */
  posterFallback?: string | null
  className?: string
}

/**
 * Progressive Matterport embed.
 *
 * Initial load ships **zero Matterport JS** — only a lightweight poster + "View 3D Tour" CTA.
 * The real iframe is initialized when:
 *   • the user clicks/taps (always), or
 *   • (desktop, fast connection) the section nears the viewport AND the browser is idle.
 * Mobile and Save-Data users get click-to-load only, keeping Matterport fully off the critical path.
 */
export function ProgressiveMatterport({ embedHtml, posterFallback, className }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(false)
  // Poster = a representative project photo of the same space, served via the Next
  // optimizer (AVIF/WebP + resized). Matterport's own thumbnail endpoint is
  // CORS/403-blocked from the browser, so we don't rely on it.
  const [posterSrc, setPosterSrc] = useState<string | null>(
    getOptimizedImageUrl(posterFallback, 640, 80) || null,
  )

  useEffect(() => {
    if (active) return
    const el = ref.current
    if (!el) return

    // Conservative on mobile / Save-Data: click-to-load only (no auto-activation).
    const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
    if (conn?.saveData) return
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) return

    const io = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return
        io.disconnect()
        const activate = () => setActive(true)
        const ric = (window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void })
          .requestIdleCallback
        if (typeof ric === 'function') ric(activate, { timeout: 3000 })
        else setTimeout(activate, 800) // fallback for browsers without requestIdleCallback
      },
      { rootMargin: '600px' }, // generous: prepare before the section is on screen
    )
    io.observe(el)
    return () => io.disconnect()
  }, [active])

  return (
    <div ref={ref} className={className || 'h-full w-full'}>
      {active ? (
        <div
          className="h-full w-full [&_iframe]:h-full [&_iframe]:w-full"
          dangerouslySetInnerHTML={{ __html: embedHtml }}
        />
      ) : (
        <button
          type="button"
          onClick={() => setActive(true)}
          aria-label="View 3D virtual tour"
          className="group relative h-full w-full overflow-hidden bg-black"
        >
          {posterSrc ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={posterSrc}
              alt="3D virtual tour preview"
              loading="lazy"
              decoding="async"
              onError={() => setPosterSrc(null)}
              className="h-full w-full object-cover opacity-90 transition-transform duration-500 ease-out group-hover:scale-105"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-[#009bab] to-[#00646f]" />
          )}
          <span className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-2.5">
            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform duration-300 group-hover:scale-110">
              <Play className="ml-0.5 h-6 w-6 fill-[#1d1d1b] text-[#1d1d1b]" />
            </span>
            <span className="rounded bg-black/55 px-3 py-1 text-[13px] font-medium tracking-wide text-white">
              View 3D Tour
            </span>
          </span>
        </button>
      )}
    </div>
  )
}
